import type { PlatformSettings } from '@nexo/types';
import { Button, Switch, buttonClasses, useToast } from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Compass, Sparkles, UsersRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/forms';
import { keys, useFeatures } from '../../lib/queries';
import { AdminPage, QueryState } from './AdminLayout';

/** Platform switches (features on/off for everyone) and Nexo's official server. */
export function AdminFeatures() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['admin', 'settings'], queryFn: api.admin.settings });
  const features = useFeatures();

  const update = useMutation({
    mutationFn: api.admin.updateSettings,
    onSuccess: (saved) => {
      queryClient.setQueryData(['admin', 'settings'], saved);
      void queryClient.invalidateQueries({ queryKey: keys.features });
      toast({ title: t('Ajuste guardado'), tone: 'success' });
    },
    onError: (err) =>
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' }),
  });
  const set = (key: keyof PlatformSettings) => (value: boolean) => update.mutate({ [key]: value });

  const official = useMutation({
    mutationFn: () => api.admin.createOfficialTeam({ addEveryone: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.features });
      void queryClient.invalidateQueries({ queryKey: keys.teams });
      toast({ title: t('Servidor oficial listo'), tone: 'success' });
    },
    onError: (err) =>
      toast({ title: t('No se pudo crear'), description: errorMessage(err), tone: 'error' }),
  });
  const officialId = features.data?.officialTeamId;

  return (
    <AdminPage
      title={t('Funciones')}
      description={t('Activa o desactiva funciones para todas las personas que usan Nexo.')}
    >
      {!settings.data ? (
        <QueryState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : (
        <div className="flex max-w-2xl flex-col gap-4">
          <section className="flex flex-col gap-4 rounded-xl border border-border p-5">
            <Switch
              icon={<Compass />}
              label={t('Explorar servidores')}
              hint={t(
                'Directorio de equipos públicos: los propietarios pueden hacer público su equipo y cualquiera puede entrar.',
              )}
              checked={settings.data.publicDirectory}
              disabled={update.isPending}
              onChange={set('publicDirectory')}
            />
            <Switch
              icon={<Sparkles />}
              label={t('Resúmenes de llamadas con IA')}
              hint={t(
                'Transcribe las llamadas que se graben, genera un resumen y propone tareas. Necesita las claves de IA configuradas en el servidor.',
              )}
              checked={settings.data.callSummaries}
              disabled={update.isPending}
              onChange={set('callSummaries')}
            />
          </section>

          <section className="rounded-xl border border-border p-5">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <BadgeCheck className="size-4 fill-accent text-surface" />
              {t('Servidor oficial de Nexo')}
            </h2>
            <p className="mt-1 text-sm text-muted">
              {t(
                'Todas las cuentas nuevas entran en él para hablar con vosotros, reportar errores y proponer ideas. Pueden salir cuando quieran.',
              )}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {officialId ? (
                <>
                  <Link
                    to={`/app/t/${officialId}`}
                    className={buttonClasses({ variant: 'secondary' })}
                  >
                    {t('Abrir el servidor oficial')}
                  </Link>
                  <Button
                    variant="ghost"
                    icon={<UsersRound className="size-4" />}
                    loading={official.isPending}
                    onClick={() => official.mutate()}
                  >
                    {t('Añadir a todas las cuentas actuales')}
                  </Button>
                </>
              ) : (
                <Button loading={official.isPending} onClick={() => official.mutate()}>
                  {t('Crear «Nexo Hub» y añadir a todos')}
                </Button>
              )}
            </div>
          </section>
        </div>
      )}
    </AdminPage>
  );
}
